import { Avatar, Form, Input } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import MasterModal from 'components/organisms/masterModal';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import s from './pap-case-details-modal.module.scss';

import type { PapCaseStatus } from '../../types';

type CaseReviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (nextStatus: PapCaseStatus, reviewNote?: string) => void;
  openingCaseStatus?: PapCaseStatus;
  isSavingData?: boolean;
  zIndex?: number;
};

const CaseReviewModal: FC<CaseReviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  openingCaseStatus,
  isSavingData,
  zIndex,
}) => {
  const { t } = useTranslation();
  const [reviewNote, setReviewNote] = useState<string>('');

  const config = {
    submitted: {
      title: t('papCase.submitToExternal'),
      subtitle: t('papCase.reviewModalSubmittedSubtitle'),
      placeholder: t('papCase.reviewModalAddNursePlaceholder'),
      confirmLabel: t('papCase.submit'),
      noteLabel: (
        <>
          {t('papCase.reviewerNote')}{' '}
          <span className={s['note-label-optional']}>(optional)</span>
        </>
      ),
      noteRequired: false,
      icon: <SendOutlined />,
      avatarStyle: {
        backgroundColor: 'var(--ant-purple-2)',
        color: 'var(--color-primary)',
      },
    },
    'needs-info': {
      title: t('papCase.requestMoreInfo'),
      subtitle: t('papCase.reviewModalNeedsInfoSubtitle'),
      placeholder: t('papCase.reviewModalSpecifyInfoPlaceholder'),
      confirmLabel: t('papCase.requestInfo'),
      noteLabel: (
        <>
          {t('papCase.reviewerNote')}
          <span className={s['note-label-required']}>*</span>
        </>
      ),
      noteRequired: true,
      icon: <WarningOutlined />,
      avatarStyle: {
        backgroundColor: 'var(--ant-orange-2)',
        color: 'var(--ant-gold-7)',
      },
    },
    denied: {
      title: t('papCase.denyCase'),
      subtitle: t('papCase.reviewModalDeniedSubtitle'),
      placeholder: t('papCase.reviewModalDenialReasonPlaceholder'),
      confirmLabel: t('papCase.deny'),
      noteLabel: (
        <>
          {t('papCase.reviewerNote')}
          <span className={s['note-label-required']}>*</span>
        </>
      ),
      noteRequired: true,
      icon: <CloseCircleOutlined />,
      avatarStyle: {
        backgroundColor: 'var(--ant-red-2)',
        color: 'var(--ant-red-5)',
      },
    },
    approved: {
      title: t('papCase.submitToExternal'),
      subtitle: t('papCase.reviewModalApprovedSubtitle'),
      placeholder: t('papCase.reviewModalAddNursePlaceholder'),
      confirmLabel: t('papCase.approve'),
      noteLabel: (
        <>
          {t('papCase.reviewerNote')}{' '}
          <span className={s['note-label-optional']}>(optional)</span>
        </>
      ),
      noteRequired: false,
      icon: <CheckCircleOutlined />,
      avatarStyle: {
        backgroundColor: 'var(--ant-green-2)',
        color: 'var(--ant-green-7)',
      },
    },
  }[openingCaseStatus as Exclude<PapCaseStatus, 'draft' | 'in-review'>];

  const handleClose = () => {
    onClose();
    setReviewNote('');
  };

  const handleConfirm = () => {
    onConfirm(
      openingCaseStatus as Exclude<PapCaseStatus, 'draft' | 'in-review'>,
      reviewNote,
    );
    setReviewNote('');
  };

  return (
    <MasterModal
      isOpen={isOpen}
      toggle={handleClose}
      title={config?.title ?? ''}
      centered
      size="medium"
      zIndex={zIndex}
      secondaryButton={{
        label: t('papCase.cancel'),
        onClick: handleClose,
      }}
      primaryButton={{
        label: config?.confirmLabel ?? '',
        onClick: handleConfirm,
        loading: isSavingData,
        disabled: !!(config?.noteRequired && !reviewNote.trim()),
      }}
    >
      <div className={s['case-review-modal']}>
        {config?.icon ? (
          <div className={s['review-modal-icon-row']}>
            <Avatar
              icon={config.icon}
              style={config.avatarStyle}
              className={s['review-modal-avatar']}
              size={40}
            />
            <p className={s['review-modal-description']}>{config.subtitle}</p>
          </div>
        ) : (
          <p>{config?.subtitle}</p>
        )}
        <Form layout="vertical">
          <Form.Item
            label={config?.noteLabel}
            className={s['review-modal-form-item']}
          >
            <Input.TextArea
              className={s['review-note-input']}
              placeholder={config?.placeholder}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              maxLength={500}
              disabled={isSavingData}
            />
          </Form.Item>
        </Form>
      </div>
    </MasterModal>
  );
};

export default CaseReviewModal;
